<!--
  BaseNumberInput Component Template
  
  Renders a BaseInput component with decimal input functionality.
  
  Features:
  - Automatic decimal validation via v-number directive
  - Automatic integer validation via v-integer directive
  - Attribute pass-through via v-bind="$attrs"
  - Slot support for prepend/append content
  - Event forwarding for input, focus, blur, and change events
  
  @slot prepend - Content to display before the input (e.g., currency symbol)
  @slot append - Content to display after the input (e.g., unit label)
-->
<template>
  <base-input
    v-number="mode"
    v-bind="$attrs"
    :value="displayValue"
    :type="'text'"
    :inputmode="'decimal'"
    :pattern="'[0-9]*\.?[0-9]*'"
    @input="handleInput"
    @focus="handleFocus"
    @blur="handleBlur"
    @change="handleChange"
  >
    <!-- Prepend slot for content before the input -->
    <template v-if="$slots.prepend" slot="prepend">
      <slot name="prepend"></slot>
    </template>

    <!-- Append slot for content after the input -->
    <template v-if="$slots.append" slot="append">
      <slot name="append"></slot>
    </template>
  </base-input>
</template>

<script>
/**
 * BaseNumberInput Component
 *
 * A specialized input component for handling decimal/numeric values.
 * Wraps the BaseInput component and provides decimal-specific validation and formatting.
 * Automatically filters non-numeric characters and converts input to numeric values.
 *
 * @component
 * @example
 * <BaseNumberInput
 *   v-model.number="price"
 *   label="Price"
 *   placeholder="Enter price"
 *   :error="validation.error"
 * />
 */
export default {
  name: "BaseNumberInput",
  inheritAttrs: false,

  /**
   * Component props definition
   * @typedef {Object} Props
   * @property {number|string} value - The input value, accepts both number and string types
   */
  props: {
    /**
     * The current value of the input
     * @type {number|string}
     * @default ""
     * @description Accepts numeric values or string representations of numbers
     */
    value: {
      type: [Number, String],
      default: ""
    },
    mode: {
      type: String,
      default: "decimal",
      validator: function(value) {
        return ["integer", "decimal"].includes(value);
      }
    }
  },

  computed: {
    /**
     * Computed property that converts the input value to a string for display
     * @returns {string} The string representation of the current value, or empty string if falsy
     * @description Safely converts numeric values to strings for proper display in the input field
     */
    displayValue() {
      const isValueEmpty =
        this.value === null || this.value === undefined || this.value === "";
      if (isValueEmpty) {
        return "";
      }
      return String(this.value);
    }
  },

  methods: {
    /**
     * Handles input events and normalizes the entered value to a valid decimal number
     * @param {string|number} value - The raw input value from the user
     * @fires input - Emits the normalized numeric value
     * @returns {void}
     * @description
     * - Returns 0 immediately if no value is provided
     * - Strips all non-numeric characters except decimal points
     * - Converts the cleaned value to a float and emits it
     * @example
     * // Input: "12.34abc" -> Output: 12.34
     * // Input: "abc" -> Output: 0
     * // Input: "123.45.67" -> Output: 123.4567
     */
    handleInput(value) {
      if (!value || value === "") {
        this.$emit("input", 0);
        return;
      }

      value = String(value);
      // Remove all non-numeric characters except decimal points
      value = value.replace(/[^0-9.]/g, "");

      this.$emit("input", parseFloat(value));
    },

    /**
     * Handles focus events and forwards them to parent components
     * @param {FocusEvent} event - The native DOM focus event object
     * @fires focus - Forwards the focus event to parent components
     * @returns {void}
     * @description Emits the focus event when the input gains focus
     */
    handleFocus(event) {
      this.$emit("focus", event);
    },

    /**
     * Handles blur events and forwards them to parent components
     * @param {FocusEvent} event - The native DOM blur event object
     * @fires blur - Forwards the blur event to parent components
     * @returns {void}
     * @description Emits the blur event when the input loses focus
     */
    handleBlur(event) {
      this.$emit("blur", event);
    },

    /**
     * Handles change events and forwards them to parent components
     * @param {Event} event - The native DOM change event object
     * @fires change - Forwards the change event to parent components
     * @returns {void}
     * @description Emits the change event when the input value has changed and the input loses focus
     */
    handleChange(event) {
      this.$emit("change", event);
    }
  }
};
</script>
